import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaFilter } from 'react-icons/fa';
import { Box, Typography } from '@mui/material';
import { skillsApi, tagsApi } from '../../../../../../services/api';
import { useAlert } from '../../../../../../contexts/AlertContext';
import { useRadarData } from '../../../../../../hooks/useRadarData';
import LoadingSpinner from '../../../../../common/LoadingSpinner';
import TreeChart from '../../../../../TrendRadarChart/components/TreeChart';
import ZoomControls from '../../../../../TrendRadarChart/components/ZoomControls';
import SearchBar from '../../../../../TrendRadarChart/components/SearchBar';
import Breadcrumb from '../../../../../TrendRadarChart/components/Breadcrumb';
import NavigationPanel from '../../../../../TrendRadarChart/components/NavigationPanel';
import SelectionPanel from '../../../../../common/SelectionPanel';
import TagSelectorModalComponent from '../../../../../common/TagSelectorModal';
import useZoomPan from '../../../../../TrendRadarChart/hooks/useZoomPan';
import useChartData from '../../../../../TrendRadarChart/hooks/useChartData';
import useSearch from '../../../../../TrendRadarChart/hooks/useSearch';
import { TREE_CHART_MODES, SEGMENT_TYPE } from '../../../../../../constants/radar';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ChartPanel,
  HeaderButton,
  ConfigurationPanel,
  ConfigurationHeader,
  ConfigurationContent
} from './styles';

const SkillRadarMapping = () => {
  const { matrixId, versionId, skillId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [skill, setSkill] = useState(null);
  const [isPanelsVisible, setIsPanelsVisible] = useState(true);
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);
  const [expandedPath, setExpandedPath] = useState([]);
  const [isNavigationPanelCollapsed, setIsNavigationPanelCollapsed] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);

  // Get radar data using the hook
  const { data: radarData, loading: radarLoading, error: radarError } = useRadarData();

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getZoomFactor,
  } = useZoomPan();

  // Check if a segment is assigned to the skill
  const isSegmentAssigned = useCallback((segmentId) => {
    if (!skill?.mappings) {
      return false;
    }
    return skill.mappings.some(
      (mapping) => mapping.segment.id=== segmentId
    );
  }, [skill]);

  // Filter data by tags
  const filterDataByTags = useCallback((data, selectedTags) => {
    if (!selectedTags.length) return data;

    const filterNodes = (node) => {
      if (!node) return null;

      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        const hasSelectedTag = selectedTags.some(selectedTag => 
          node.tags?.some(nodeTag => 
            nodeTag.id === selectedTag.id || 
            nodeTag.parentTagId === selectedTag.id
          )
        );

        return hasSelectedTag ? node : null;
      }

      const filteredSubcategories = (node.subcategories || [])
        .map(child => filterNodes(child))
        .filter(Boolean);

      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    return filterNodes(data);
  }, []);

  // Update getFilteredData to include tag filtering
  const getFilteredData = useCallback(() => {
    if (!radarData) return null;

    let filteredData = radarData;

    // First apply selection filter if needed
    if (showOnlySelected) {
      const filterBySelection = (node) => {
        if (!node) return null;

        if (node.segmentType === SEGMENT_TYPE.LEAF) {
          return isSegmentAssigned(node.leafSegmentId) ? node : null;
        }

        const filteredSubcategories = (node.subcategories || [])
          .map(filterBySelection)
          .filter(Boolean);

        return filteredSubcategories.length > 0
          ? { ...node, subcategories: filteredSubcategories }
          : null;
      };

      filteredData = filterBySelection(filteredData);
    }

    // Then apply tag filter
    return filterDataByTags(filteredData, selectedTags);
  }, [radarData, showOnlySelected, isSegmentAssigned, filterDataByTags, selectedTags]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  const {
    selectedSubcategory,
    handleSelection,
    handleBack,
  } = useChartData(filteredData || {}, TREE_CHART_MODES.TREE);

  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch
  } = useSearch(filteredData || {}, selectedSubcategory);

  // Fetch tags
  useEffect(() => {
    const fetchTags = async () => {
      setIsTagsLoading(true);
      try {
        const tagsData = await tagsApi.getTagHierarchy();
        setTags(tagsData);
      } catch (error) {
        console.error('Failed to load tags:', error);
        showAlert('Failed to load tags', 'error', error);
      } finally {
        setIsTagsLoading(false);
      }
    };

    fetchTags();
  }, [showAlert]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  const handleTagSelectorToggle = useCallback(() => {
    setIsTagSelectorOpen(!isTagSelectorOpen);
  }, [isTagSelectorOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const skillData = await skillsApi.getSkillTemplate(versionId, skillId);
        
        setSkill({
          ...skillData,
          skillName: skillData.skill.name,
          mappings: skillData.mappings
        });
      } catch (error) {
        showAlert('Failed to fetch skill data', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [skillId, versionId, showAlert]);

  const handleSegmentToggle = async (segmentId) => {
    try {
      if (isSegmentAssigned(segmentId)) {
        await skillsApi.removeSegmentFromSkillTemplate(versionId, skillId, segmentId);
        showAlert('Segment removed successfully', 'success');
      } else {
        await skillsApi.attachSegmentsToSkillTemplate(versionId, skillId, { segmentIds: [segmentId] });
        showAlert('Segment attached successfully', 'success');
      }
      
      // Refresh skill data
      const updatedSkill = await skillsApi.getSkillTemplate(versionId, skillId);
      setSkill(updatedSkill);
    } catch (error) {
      showAlert('Failed to update segment', 'error', error);
    }
  };

  if (loading || radarLoading) {
    return <LoadingSpinner />;
  }

  if (radarError) {
    showAlert('Failed to load radar data', 'error');
    return null;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(`/skills-matrix/templates/${matrixId}/versions/${versionId}/configure`)}>
            <FaArrowLeft /> Back to Skills Selection
          </BackButton>
          <Title>Configure Segments | {skill?.skillName}</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setIsPanelsVisible(!isPanelsVisible)}>
            {isPanelsVisible ? <FaEyeSlash /> : <FaEye />}
            {isPanelsVisible ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content $isPanelsVisible={isPanelsVisible} style={{ 
        marginTop: isPanelsVisible ? '0' : '0',
        gap: isPanelsVisible ? '12px' : '0' 
      }}>
        <NavigationPanel
          isExpanded={!isNavigationPanelCollapsed}
          onToggle={() => setIsNavigationPanelCollapsed(!isNavigationPanelCollapsed)}
          searchBar={
            <div style={{ flex: 1 }}>
              <SearchBar
                searchTerm={searchTerm}
                searchResults={searchResults}
                showResults={showResults}
                isGlobalSearch={isGlobalSearch}
                selectedSubcategory={selectedSubcategory}
                onSearch={handleSearch}
                onResultSelect={(result) => {
                  const { selectedNode, pathToNode } = handleResultSelect(result);
                  setExpandedPath(pathToNode);
                  handleSelection(selectedNode);
                }}
                onToggleSearchMode={() => setIsGlobalSearch(!isGlobalSearch)}
              />
            </div>
          }
          breadcrumb={
            <Breadcrumb
              data={filteredData || {}}
              selectedSubcategory={selectedSubcategory}
              onNavigate={handleSelection}
            />
          }
          isVisible={isPanelsVisible}
          configMode={TREE_CHART_MODES.EMPLOYEE}
          chartType={TREE_CHART_MODES.TREE}
          selectedCount={skill?.mappings?.length || 0}
          showOnlySelected={showOnlySelected}
          onToggleShowSelected={() => setShowOnlySelected(!showOnlySelected)}
          onTagSelect={handleTagSelectorToggle}
          selectedTagsCount={selectedTags.length}
        />

        <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
          <ChartPanel>
            <div style={{ position: 'relative', height: '100%' }}>
              <TreeChart
                data={filteredData}
                selectedSubcategory={selectedSubcategory}
                hoveredItem={null}
                onHover={() => {}}
                onMouseOut={() => {}}
                onSelection={handleSelection}
                onBack={handleBack}
                zoomFactor={treeZoomFactor}
                configMode={TREE_CHART_MODES.SKILL_ASSOCIATION}
                expandedPath={expandedPath}
                setExpandedPath={setExpandedPath}
                isLeafAssigned={isSegmentAssigned}
                initialCheckedLeafs={new Set(skill?.mappings?.map(m => m.segment.segmentId) || [])}
                onLeafToggle={handleSegmentToggle}
                isTreeExpanded={isTreeExpanded}
                onTreeExpansionToggle={setIsTreeExpanded}
              />
              <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '1rem' }}>
                <ZoomControls
                  zoomFactor={treeZoomFactor}
                  onZoomIn={(isMax) => handleZoomIn(TREE_CHART_MODES.TREE, isMax)}
                  onZoomOut={() => handleZoomOut(TREE_CHART_MODES.TREE)}
                  onReset={() => handleResetPosition(TREE_CHART_MODES.TREE)}
                />
              </div>
            </div>
          </ChartPanel>
        </Box>

        {isTagSelectorOpen && (
          <TagSelectorModalComponent
            isOpen={isTagSelectorOpen}
            onClose={() => setIsTagSelectorOpen(false)}
            tags={tags}
            selectedTags={selectedTags}
            onTagToggle={handleTagToggle}
          />
        )}
      </Content>
    </Container>
  );
};

export default SkillRadarMapping; 